// src/components/app-sidebar.tsx
//
// UPDATED — flat "Platform" group split into three grouped sections
// (Configuration / Activity / Record), per the four-group IA decision.
// Home and Chat remain ungrouped, rendered in their own top group —
// Chat deliberately excluded from the grouped structure entirely (it
// spans every group's capability rather than being peer to any one of
// them; see buildlog discussion). Same rendering logic (active-state
// computation, SidebarMenuSub for nested items) reused verbatim per
// group — this file has no NavMain/Collapsible dependency, so grouping
// is done via multiple SidebarGroup blocks, not a different component.
//
// FIXED — TS2339/TS2345. navTop's items were all empty array literals
// with no type annotation, so TypeScript inferred items: never[] for
// that array — and since renderMenu's parameter type was inferred from
// whichever array it saw first (typeof navConfiguration or similar),
// every other array with real sub-items failed to satisfy that
// inferred never[] shape. Fixed by giving NavItem/NavSubItem explicit
// interfaces and annotating every nav array and renderMenu's parameter
// with NavItem[] directly, rather than relying on structural inference
// from any one array.
//
// UPDATED — added "Engagements" to navActivity, linking to the new
// /dashboard/engagements list view (DashboardEngagements.tsx). Placed
// in Activity, alongside Timeline/Approvals/Detection — engagements
// are ongoing work, same category as the rest of that group. Uses the
// existing isItemActive logic unchanged: it already highlights this
// item correctly for both the list view AND a specific engagement
// detail page (/dashboard/engagements/:engagementRef), since that path
// starts with the list route's own url — no new active-state logic
// needed.

import * as React from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  Home, MessagesSquare, Package, LayoutDashboard, ShieldCheck,
  CheckSquare, FileCheck2, Store, BarChart3, Settings as SettingsIcon,
  SearchCode, Handshake,
} from 'lucide-react';
import { UserProfile, ViewState } from '../types';
import { NavUser } from './nav-user';
import {
  Sidebar, SidebarContent, SidebarFooter, SidebarGroup, SidebarGroupContent,
  SidebarGroupLabel, SidebarHeader, SidebarMenu, SidebarMenuButton,
  SidebarMenuItem, SidebarMenuSub, SidebarMenuSubButton, SidebarMenuSubItem,
} from './ui/sidebar';

interface NavSubItem {
  title: string;
  url: string;
}

interface NavItem {
  title: string;
  url: string;
  icon: React.ComponentType<{ className?: string }>;
  items: NavSubItem[];
}

// Ungrouped — Home is the index route, Chat sits outside the grouped
// structure entirely per the "spans every group" decision.
const navTop: NavItem[] = [
  { title: 'Home', url: '/dashboard', icon: Home, items: [] },
  { title: 'Chat', url: '/dashboard/chat-home', icon: MessagesSquare, items: [] },
];

const navConfiguration: NavItem[] = [
  { title: 'Profile', url: '/dashboard/profile', icon: Store, items: [] },
  { title: 'Outcomes', url: '/dashboard/outcomes', icon: Package, items: [] },
  { title: 'Rules', url: '/dashboard/rules', icon: ShieldCheck, items: [] },
  { title: 'Settings', url: '/dashboard/settings', icon: SettingsIcon, items: [] },
];

const navActivity: NavItem[] = [
  {
    title: 'Timeline', url: '/dashboard/timeline', icon: LayoutDashboard,
    items: [
      { title: 'Needs Approval', url: '/dashboard/timeline/needs-approval' },
      { title: 'In Progress', url: '/dashboard/timeline/in-progress' },
      { title: 'Completed', url: '/dashboard/timeline/completed' },
    ],
  },
  { title: 'Approvals', url: '/dashboard/approvals', icon: CheckSquare, items: [] },
  { title: 'Detection', url: '/dashboard/detection', icon: SearchCode, items: [] },
  { title: 'Engagements', url: '/dashboard/engagements', icon: Handshake, items: [] },
];

const navRecord: NavItem[] = [
  { title: 'Audit & Proof', url: '/dashboard/audit', icon: FileCheck2, items: [] },
  { title: 'Analytics', url: '/dashboard/analytics', icon: BarChart3, items: [] },
];

interface AppSidebarProps extends React.ComponentProps<typeof Sidebar> {
  user: UserProfile;
  onNavigate: (view: ViewState) => void;
}

export function AppSidebar({ user, onNavigate, ...props }: AppSidebarProps) {
  const location = useLocation();

  const isItemActive = (url: string) =>
    url === '/dashboard'
      ? location.pathname === '/dashboard'
      : location.pathname === url || location.pathname.startsWith(url + '/');

  // Explicit NavItem[] parameter — do NOT change back to
  // `typeof navConfiguration` or similar; that's exactly what caused
  // TS2339/TS2345, since inference from one array's literal shape does
  // not widen correctly across sibling arrays with different item
  // counts. This annotation is load-bearing, not stylistic.
  const renderMenu = (items: NavItem[]) => (
    <SidebarMenu>
      {items.map((item) => {
        const isActive = isItemActive(item.url);
        return (
          <SidebarMenuItem key={item.title}>
            <SidebarMenuButton
              isActive={isActive}
              render={<Link to={item.url} />}
            >
              <item.icon className="size-4" />
              <span>{item.title}</span>
            </SidebarMenuButton>
            {item.items.length > 0 && (
              <SidebarMenuSub>
                {item.items.map((sub) => (
                  <SidebarMenuSubItem key={sub.title}>
                    <SidebarMenuSubButton
                      isActive={location.pathname === sub.url}
                      render={<Link to={sub.url} />}
                    >
                      {sub.title}
                    </SidebarMenuSubButton>
                  </SidebarMenuSubItem>
                ))}
              </SidebarMenuSub>
            )}
          </SidebarMenuItem>
        );
      })}
    </SidebarMenu>
  );

  return (
    <Sidebar collapsible="icon" {...props}>
      <SidebarHeader>
        {/* Org/logo slot — intentionally empty for now. */}
      </SidebarHeader>

      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupContent>{renderMenu(navTop)}</SidebarGroupContent>
        </SidebarGroup>

        <SidebarGroup>
          <SidebarGroupLabel>Configuration</SidebarGroupLabel>
          <SidebarGroupContent>{renderMenu(navConfiguration)}</SidebarGroupContent>
        </SidebarGroup>

        <SidebarGroup>
          <SidebarGroupLabel>Activity</SidebarGroupLabel>
          <SidebarGroupContent>{renderMenu(navActivity)}</SidebarGroupContent>
        </SidebarGroup>

        <SidebarGroup>
          <SidebarGroupLabel>Record</SidebarGroupLabel>
          <SidebarGroupContent>{renderMenu(navRecord)}</SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      <SidebarFooter>
        <NavUser user={user} onNavigate={onNavigate} />
      </SidebarFooter>
    </Sidebar>
  );
}